import { ArrowRight, FileText, ListFilter, Scissors, ShieldCheck, Table2 } from 'lucide-react';
import { EmailIntakeCard } from '../paige/EmailIntakeCard';
import { RetentionCard } from '../paige/RetentionCard';
import { SendResultsCard } from '../paige/SendResultsCard';
import './setup-workbench.css';

type SetupDestination = 'document-types' | 'document-breaks' | 'review' | 'lookups';
type TypePreview = {
  id: string;
  name: string;
  fields: readonly { id: string; name: string; kind: string }[];
};

/** A map of the configured job, not a learning score or a second settings owner. */
export function SetupOverview({
  types,
  separation,
  separationReady,
  reviewSummary,
  lookupCount,
  lookupError,
  onOpen,
  onOpenType,
}: {
  types: readonly TypePreview[];
  separation: string;
  separationReady: boolean;
  reviewSummary: string;
  lookupCount: number;
  lookupError: boolean;
  onOpen: (section: SetupDestination) => void;
  onOpenType: (id: string) => void;
}) {
  const shownTypes = types.slice(0, 6);
  const fieldCount = types.reduce((sum, type) => sum + type.fields.length, 0);
  const settings = [
    {
      id: 'document-breaks' as const,
      icon: Scissors,
      title: 'Separate documents',
      detail: separationReady ? separation : 'Barcode value needed',
      attention: !separationReady,
    },
    { id: 'review' as const, icon: ShieldCheck, title: 'Ask for review', detail: reviewSummary },
    {
      id: 'lookups' as const,
      icon: Table2,
      title: 'Fill from lists',
      detail: lookupError
        ? 'Lists could not be read'
        : lookupCount > 0
          ? `${lookupCount.toLocaleString()} saved ${lookupCount === 1 ? 'lookup' : 'lookups'}`
          : 'Connect fields to reference data',
      attention: lookupError,
    },
  ];
  return (
    <div className="setup-map">
      <section className="setup-map-types" aria-label="Configured document types and fields">
        <header>
          <div>
            <h2>What this job captures</h2>
            <p>
              {types.length.toLocaleString()}{' '}
              {types.length === 1 ? 'document type' : 'document types'}
              <span aria-hidden="true"> · </span>
              {fieldCount.toLocaleString()} {fieldCount === 1 ? 'field' : 'fields'}
            </p>
          </div>
          <button type="button" className="setup-map-open" onClick={() => onOpen('document-types')}>
            <ListFilter size={17} />
            {types.length > 6
              ? `All ${types.length.toLocaleString()} types`
              : 'Edit types & fields'}
            <ArrowRight size={17} />
          </button>
        </header>
        {shownTypes.length > 0 ? (
          <div className="setup-map-type-grid">
            {shownTypes.map((type) => (
              <button
                type="button"
                className="setup-map-type"
                key={type.id}
                onClick={() => onOpenType(type.id)}
                aria-label={`Edit ${type.name} fields`}
                aria-description={`${type.fields.length} configured fields: ${type.fields.map((field) => field.name).join(', ')}`}
              >
                <span className="setup-map-type-heading">
                  <span className="setup-map-paper-icon">
                    <FileText size={27} aria-hidden="true" />
                  </span>
                  <span>
                    <strong>{type.name}</strong>
                    <small>
                      {type.fields.length.toLocaleString()}{' '}
                      {type.fields.length === 1 ? 'field' : 'fields'}
                    </small>
                  </span>
                  <ArrowRight size={18} aria-hidden="true" />
                </span>
                <span className="setup-map-field-preview" aria-hidden="true">
                  {type.fields.slice(0, 4).map((field) => (
                    <span key={field.id}>
                      <i>{field.kind === 'line-items' ? <Table2 size={14} /> : <span />}</i>
                      <span>{field.name}</span>
                      <b />
                    </span>
                  ))}
                  {type.fields.length === 0 && <em>No fields configured</em>}
                  {type.fields.length > 4 && (
                    <em>+{(type.fields.length - 4).toLocaleString()} more fields</em>
                  )}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="setup-map-empty">
            <FileText size={44} aria-hidden="true" />
            <h3>Start with a document type</h3>
            <p>Choose the information this job should capture.</p>
            <button
              type="button"
              className="primary-button"
              onClick={() => onOpen('document-types')}
            >
              Set up types & fields
              <ArrowRight size={17} />
            </button>
          </div>
        )}
        {types.length > shownTypes.length && (
          <p className="setup-map-subset">
            Showing {shownTypes.length} of {types.length.toLocaleString()} configured types
          </p>
        )}
      </section>
      <aside className="setup-map-settings" aria-label="Job behavior">
        <h2>How it works</h2>
        {settings.map(({ id, icon: Icon, title, detail, attention }) => (
          <button
            type="button"
            key={id}
            className={`setup-map-setting${attention ? ' attention' : ''}`}
            onClick={() => onOpen(id)}
          >
            <Icon size={22} aria-hidden="true" />
            <span>
              <strong>{title}</strong>
              <small>{detail}</small>
            </span>
            <ArrowRight size={17} aria-hidden="true" />
          </button>
        ))}
        <EmailIntakeCard />
        <RetentionCard />
        <SendResultsCard />
      </aside>
    </div>
  );
}
